import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form, FormGroup, Input, Label, Button } from 'reactstrap';
import Body from '../Components/layout/Body';
import LearnMoreModal from '../Components/layout/LearnMoreModal';
import { getDetailsTableHTML } from '../Components/layout/DisplayTestDriveInfo';
import { UserTextOrNone } from '../Components/layout/DisplayUserText';

const NEXTSTEPS_PICKS_HELP = `
  <div>
    <p><strong>Purpose:</strong> Document some clear next steps, so you and the team know where to go from here.</p>
    <p>At this point, you've selected at least one access method as a Pick.  A Pick is just a method that you've tried and that has some promise.</p>
    <p>Possible next steps here could vary quite a bit, depending on how well this Pick performed for the client and how far along in the
    process you are.</p>
    <p>Here are some example scenarios and corresponding next steps:</p>
    <p><strong>Scenario 1. We know for sure that this Pick is a great fit -- let's go with it!</strong></p>
    <p>Next steps might be:</p>
    <ol>
      <li>Recommend this method for purchase and implementation. Consider the entire system and services that will be needed for success.</li>
      <li>Order the device and any associated accessories. (Find funding would be a step in some cases, too.)</li>
      <li>Deliver the system</li>
      <li>Do initial training and set up a schedule for practice and learning</li>
      <li>Establish criteria for successful onboarding</li>
    </ol>
    <p><strong>Scenario 2. This method has some promise, but we're not quite sure yet.</strong></p>
    <p>Next steps might be:</p>
    <ol>
      <li>Document the reasons why we're still unsure and define a plan to address those reasons</li>
      <li>Provide client with a loaner device to use and practice with</li>
      <li>Establish a specific schedule for practice and learning, and criteria for success</li>
    </ol>
    <p>These are just examples, and your real next steps would probably be more specific and actionable.  But we hope that conveys what we're getting at with this screen.</p>
    <p>
    This information will be included in the report at the end of this section. 
    </p>
    <p><strong>Note:</strong> If you need to coordinate next steps with other team members or bring in someone to assess seating / positioning, that would be a good thing to add in Next Steps.</p> 
  </div>
`
const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  picks: PropTypes.arrayOf(PropTypes.object),
  onChangeNextsteps: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onRemovePick: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  picks: []
};

//content sub-component with unique content for this page
function ReviewTabNextstepsPicksContent(props) {
  const {
      firstName,
      picks,
      onChangeNextsteps,
      onRemovePick
  } = props;


  //just in case we get to this view without any Picks:
  if(picks.length === 0){
    return(
      <>
      <p><strong>Note:</strong> There are no Picks shown here because you haven't chosen any.</p>
      </>
      );
  }
  //the typical case where we do have Picks:
  return(
    <>
    <p><strong>Enter your recommendations for next steps with each Pick here:</strong></p>
    {picks.map((pick,ix) => 
      <Row key={ix} className="aa-review-row aa-aligned-row border-top">
        <Col xs="3">        
          <div><UserTextOrNone userText={pick.setupName ? pick.setupName : pick.accessMethodName}/></div>
          <div><LearnMoreModal 
            linkText="more&gt;&gt;"
            title="Details for this Pick"
            buttonClassName="btn-sm"
            content={getDetailsTableHTML(pick)}
          /></div>
        </Col> 
        <Col xs="6">
          <Form>
            <FormGroup>
              <Label for={createFieldId(ix, 0)} className="aa-minor-note">
                Your recommended next steps:
              </Label>
              <Input
                id={createFieldId(ix, 0)}
                value={pick.nextSteps}
                onChange={onChangeNextsteps}
                name={"nextSteps-"+ix}
                type="textarea"
              />
            </FormGroup>
          </Form>
        </Col>
        <Col xs="3" className="d-flex align-items-center justify-content-end">
          <Button className="aa-inner-button" color="secondary" outline onClick={() => onRemovePick(ix)}>Revert to Prospects</Button>
        </Col>
      </Row>

    )}

    {/* <TestDriveInfoTable
      testDrives={prospects}
      showDetailsCol={true}
      showCompareCol={false}
      showAddRemoveCol={true}
      onRemoveProspect={onRemoveProspect}
      onAddPick={onAddPick}
    /> */}
    </>
  );
}

function createFieldId(pIndex, bIndex){
  return(
    "pick"+pIndex+"-field"+bIndex
  );
}
//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function ReviewTabNextstepsPicks(props) {
  const {
    firstName,
    pronouns,
    picks,
    onContinue,
    onChangeNextsteps,
    onRemovePick,
    saveFormData
  } = props;

  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "fa-thumbs-o-up",
    pageSymbolClassName: "aa-section-symbol review",
    title: `Consider next steps for Your Picks`,
    subTitle: `Your Picks are in! You're making progress!`,
    learnMore: {linkText: "Learn More about possible next steps and what to consider",
                title: "Possible next steps for a Pick",
                content: NEXTSTEPS_PICKS_HELP
                },
    nextRoute: '', 
    sidebar: false
  }

  console.log("Rendering ReviewTabNextstepsPicks");

  useEffect(() => {
    console.log('ReviewTabChooseNextstepsPicks mounted');
    return () => {
      console.log('ReviewTabNextstepsPicks unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <ReviewTabNextstepsPicksContent 
          firstName = {firstName}
          pronouns={pronouns}
          picks={picks}
          onRemovePick={onRemovePick}
          onChangeNextsteps={onChangeNextsteps}
        />
      } 
      onContinue={onContinue} 
    />
    </>
  );
}

ReviewTabNextstepsPicks.propTypes = propTypes;
ReviewTabNextstepsPicks.defaultProps = defaultProps;

export default ReviewTabNextstepsPicks;